import { getRumOrg } from './rumOrg';

declare global {
  interface Window {
    DD_RUM: any;
    DD_LOGS: any;
  }
}

// Determine environment and host based on hostname
const hostname = window.location.hostname;
let env = 'dev';
let hostName = 'dev-bitsburg';
let tracingUrls: (string | RegExp | ((_url: string) => boolean))[] = [
  'https://bitsburgdev.net',
  /https:\/\/.*\.bitsburgdev\.net/,
  (url: string) => url.startsWith('https://bitsburgdev.net'),
  'https://bitsburgdev.net/api/*',
];

if (hostname === 'bitsburg.io' || hostname.includes('prod-bitsburg')) {
  env = 'prod';
  hostName = 'prod-bitsburg';
  tracingUrls = [
    'https://bitsburg.io',
    /https:\/\/.*\.bitsburg\.io/,
    (url: string) => url.startsWith('https://bitsburg.io'),
    'https://bitsburg.io/api/*',
  ];
}

// Shared config. The org (11287 by default) is picked per session, see rumOrg.ts
const rumOrg = getRumOrg();
const SERVICE_NAME = 'bitsburg-web-ui';

// Initialize Datadog services
async function initializeDatadog() {
  // Version format must match sourcemap upload: YYYY.MM.DD.HH (e.g., 2026.01.07.15)
  const now = new Date();
  const fallbackVersion = `${now.getFullYear()}.${String(now.getMonth() + 1).padStart(2, '0')}.${String(now.getDate()).padStart(2, '0')}.${String(now.getHours()).padStart(2, '0')}`;
  const baseVersion = import.meta.env.VITE_APP_VERSION || fallbackVersion;
  let finalVersion = baseVersion;

  // 25% chance to add "-beta" suffix
  if (Math.random() < 0.25) {
    finalVersion = `${baseVersion}-beta`;
    console.debug(`Applying beta version suffix: ${baseVersion} -> ${finalVersion}`);
  } else {
    console.debug(`Using base version: ${baseVersion}`);
  }

  // Same context on RUM and Logs so both match backend traces and carry the selected org
  const globalContext = {
    host: hostName,
    rum_org: rumOrg.orgId,
    infrastructure: {
      hostname: hostName,
      environment: env,
    },
    git: {
      repository_url: import.meta.env.DD_GIT_REPOSITORY_URL || 'https://github.com/ddoghq/shopist',
      commit: {
        sha: import.meta.env.DD_GIT_COMMIT_SHA || 'unknown',
      },
    },
  };

  // Initialize RUM with the final version (including FF suffix if available)
  if (typeof window.DD_RUM !== 'undefined') {
    window.DD_RUM.init({
      clientToken: rumOrg.clientToken,
      applicationId: rumOrg.applicationId,
      site: rumOrg.site,
      service: SERVICE_NAME,
      env: env,
      version: finalVersion,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      traceSampleRate: 100,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'allow',
      allowedTracingUrls: tracingUrls,
      enableExperimentalFeatures: ["long_animation_frame", "profiling"],
      profilingSampleRate: 100
    });

    // Set global context with host information to match backend traces
    window.DD_RUM.setGlobalContext(globalContext);
  }

  // Initialize Datadog Logs
  if (typeof window.DD_LOGS !== 'undefined') {
    console.debug('DD_LOGS is available, configuring...');
    window.DD_LOGS.init({
      clientToken: rumOrg.clientToken,
      site: rumOrg.site,
      service: SERVICE_NAME,
      env: env,
      version: finalVersion,
      sessionSampleRate: 100,
      forwardErrorsToLogs: true,
      forwardConsoleLogs: ['error'],
    });

    window.DD_LOGS.setGlobalContext(globalContext);
  } else {
    console.debug('DD_LOGS is not available - logs will not be sent to Datadog');
  }
}

// Start initialization
initializeDatadog();

// Show adblock alert if RUM is blocked
window.addEventListener('load', function () {
  if (typeof window.DD_RUM === 'undefined') {
    const alertDiv = document.createElement('div');
    alertDiv.id = 'adblock-alert';
    alertDiv.className =
      'alert alert-danger alert-dismissible fade show position-fixed start-50 translate-middle-x border border-5 border-danger';
    alertDiv.setAttribute('role', 'alert');
    alertDiv.style.cssText = 'z-index: 200; top: 80px;';
    alertDiv.innerHTML = `
      <strong>Warning!</strong> It looks like you have an adblocker enabled. Please disable it, and refresh the page.
      <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
    `;
    document.body.appendChild(alertDiv);
  }
});

// Helper to set user context for trace correlation
export const setUserContext = (userId: string, userInfo?: Record<string, any>) => {
  if (typeof window.DD_RUM !== 'undefined') {
    // Extract first and last name from username (e.g., "bella.barker" or "bella.barker42")
    const usernameWithoutNumbers = userId.replace(/\d+$/, ''); // Remove trailing numbers
    const [firstName = 'User', lastName = 'Name'] = usernameWithoutNumbers.split('.');
    const capitalizedFirstName = firstName.charAt(0).toUpperCase() + firstName.slice(1);
    const capitalizedLastName = lastName.charAt(0).toUpperCase() + lastName.slice(1);
    const fullName = `${capitalizedFirstName} ${capitalizedLastName}`;
    const email = `${userId}@bitsburg.io`;

    window.DD_RUM.setUser({
      id: userId,
      name: fullName,
      email: email,
      ...userInfo,
    });
  }
};

// Helper to add global context for trace correlation
export const setGlobalContext = (key: string, value: any) => {
  if (typeof window.DD_RUM !== 'undefined') {
    window.DD_RUM.setGlobalContextProperty(key, value);
  }
};

// Helper to track user journey steps for better correlation
export const trackUserJourney = (journeyStep: string, metadata?: Record<string, any>) => {
  if (typeof window.DD_RUM !== 'undefined') {
    // Use the journey step as the action name for easier graphing in Product Analytics
    window.DD_RUM.addAction(journeyStep, {
      timestamp: new Date().toISOString(),
      ...metadata,
    });
  }
};

// Start a duration vital for measuring user flow timing
export const startDurationVital = (name: string, options?: { description?: string; context?: Record<string, any> }) => {
  if (typeof window.DD_RUM !== 'undefined' && window.DD_RUM.startDurationVital) {
    window.DD_RUM.startDurationVital(name, options);
  }
};

// Stop a duration vital and record the measurement
export const stopDurationVital = (name: string, options?: { context?: Record<string, any> }) => {
  if (typeof window.DD_RUM !== 'undefined' && window.DD_RUM.stopDurationVital) {
    window.DD_RUM.stopDurationVital(name, options);
  }
};

export {};
